import React from 'react';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Login from './components/Login';
import Register, { registerLoader } from './components/Register';
import HomePage, { homePageLoader } from './pages/HomePage';
import UserProfilePage, { userProfileLoader, userProfileUpdateAction } from './pages/UserProfilePage';
import ContractorProfilePage, { contractorProfileLoader, contractorProfileUpdateAction } from './pages/ContractorProfilePage';
import CredentialsPage, { credentialsLoader } from './pages/CredentialsPage';
import ResourceCataloguePage from './pages/ResourceCataloguePage';
import ContractorListPage, { contractorListLoader } from './pages/ContractorListPage';
import ContractorDetailPage, { contractorDetailLoader } from './pages/ContractorDetailPage';
import UserFavoriteContractorsPage, { userFavoriteContractorsLoader } from './pages/UserFavoriteContractorsPage';
import MessagesPage from './pages/MessagesPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminContractorsPage from './pages/AdminContractorsPage';
import AdminCategoriesPage from './pages/AdminCategoriesPage';
import AdminContractorReviewPage from './pages/AdminContractorReviewPage';
import AdminModeratorsPage from './pages/AdminModeratorsPage';
import AdminJobsPage from './pages/AdminJobsPage';
import CategoriesPage, { categoriesPageLoader } from './pages/CategoriesPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmailConfirmationPage from './pages/EmailConfirmationPage';
import JobListPage, { jobListLoader } from './pages/JobListPage';
import JobDetailPage, { jobDetailLoader } from './pages/JobDetailPage';
import MyJobsPage, { myJobsLoader } from './pages/MyJobsPage';
import CreateJobPage, { createJobLoader } from './pages/CreateJobPage';
import CreateDirectJobPage, { createDirectJobLoader } from './pages/CreateDirectJobPage';
import MyDirectJobsPage from './pages/MyDirectJobsPage';
import DirectJobDetailPage from './pages/DirectJobDetailPage';
import AcceptDirectJobInvitePage from './pages/AcceptDirectJobInvitePage';
import AdminDirectJobsPage from './pages/AdminDirectJobsPage';

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthProvider>
        {/* TopAppBar and BottomNavBar are now part of HomePage */}
        <Outlet />
      </AuthProvider>
    ),
    children: [
      {
        index: true,
        element: <HomePage />,
        loader: homePageLoader,
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'privacy-policy',
        element: <PrivacyPolicyPage />,
      },
      {
        path: 'terms',
        element: <TermsPage />,
      },
      {
        path: 'register',
        element: <Register />,
        loader: registerLoader,
      },
      {
        path: 'forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: 'reset-password',
        element: <ResetPasswordPage />,
      },
      {
        path: 'confirm-email',
        element: <EmailConfirmationPage />,
      },
      {
        path: 'accept-invite',
        element: <AcceptDirectJobInvitePage />,
      },
      {
        path: 'profile',
        element: (
          <PrivateRoute>
            <UserProfilePage />
          </PrivateRoute>
        ),
        loader: userProfileLoader,
        action: userProfileUpdateAction,
      },
      {
        path: 'contractor-profile',
        element: (
          <PrivateRoute>
            <ContractorProfilePage />
          </PrivateRoute>
        ),
        loader: contractorProfileLoader,
        action: contractorProfileUpdateAction,
      },
      {
        path: 'contractor-profile/credentials',
        element: (
          <PrivateRoute>
            <CredentialsPage />
          </PrivateRoute>
        ),
        loader: credentialsLoader,
      },
      {
        path: 'resource-catalogue',
        element: (
          <PrivateRoute>
            <ResourceCataloguePage />
          </PrivateRoute>
        ),
      },
      {
        path: 'direct-jobs',
        element: (
          <PrivateRoute>
            <MyDirectJobsPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'direct-jobs/new',
        element: (
          <PrivateRoute>
            <CreateDirectJobPage />
          </PrivateRoute>
        ),
        loader: createDirectJobLoader,
      },
      {
        path: 'direct-jobs/:id',
        element: (
          <PrivateRoute>
            <DirectJobDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'contractors',
        element: <ContractorListPage />,
        loader: contractorListLoader,
      },
      {
        path: 'contractors/:contractorId',
        element: <ContractorDetailPage />,
        loader: contractorDetailLoader,
      },
      {
        path: 'favorites',
        element: (
          <PrivateRoute>
            <UserFavoriteContractorsPage />
          </PrivateRoute>
        ),
        loader: userFavoriteContractorsLoader,
      },
      {
        path: 'messages',
        element: (
          <PrivateRoute>
            <MessagesPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'jobs',
        element: <JobListPage />,
        loader: jobListLoader,
      },
      {
        path: 'jobs/new',
        element: (
          <PrivateRoute>
            <CreateJobPage />
          </PrivateRoute>
        ),
        loader: createJobLoader,
      },
      {
        path: 'jobs/:jobId',
        element: <JobDetailPage />,
        loader: jobDetailLoader,
      },
      {
        path: 'my-jobs',
        element: (
          <PrivateRoute>
            <MyJobsPage />
          </PrivateRoute>
        ),
        loader: myJobsLoader,
      },
      {
        path: 'categories',
        element: <CategoriesPage />,
        loader: categoriesPageLoader,
      },
      {
        path: 'contact',
        element: <ContactPage />,
      },
      {
        path: 'about',
        element: <AboutPage />,
      },
      {
        path: 'admin',
        element: (
          <PrivateRoute requiredRole="Admin">
            <AdminDashboardPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/users',
        element: (
          <PrivateRoute requiredRole="Admin">
            <AdminUsersPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/contractors',
        element: (
          <PrivateRoute requiredRole="Admin">
            <AdminContractorsPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/categories',
        element: (
          <PrivateRoute requiredRole="Admin">
            <AdminCategoriesPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/contractors/:contractorId',
        element: (
          <PrivateRoute requiredRole={['Admin', 'Moderator']}>
            <AdminContractorReviewPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/moderators',
        element: (
          <PrivateRoute requiredRole="Admin">
            <AdminModeratorsPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/jobs',
        element: (
          <PrivateRoute requiredRole={['Admin', 'Moderator']}>
            <AdminJobsPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/direct-jobs',
        element: (
          <PrivateRoute requiredRole={['Admin', 'Moderator']}>
            <AdminDirectJobsPage />
          </PrivateRoute>
        ),
      },
    ],
  },
]);

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <RouterProvider router={router} />
    </HelmetProvider>
  );
};

export default App;